import React, { useEffect, useState } from 'react';
import { submitRecord, getCompensationOptions, getApprovingSupervisors } from '../api';
import AppHeader from './AppHeader';
import {
  TICKET_TYPES,
  initialFormData,
  buildFormDataFromPrefill,
  clearResolutionFields,
} from '../utils/submitForm';

function Submit({ user, prefill, onLogout, onNavigate }) {
  const [formData, setFormData] = useState(() => buildFormDataFromPrefill(prefill));
  const [resolutionOptions, setResolutionOptions] = useState([]);
  const [approvingSupervisors, setApprovingSupervisors] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(true);

  useEffect(() => {
    setFormData(buildFormDataFromPrefill(prefill));
    setSuccess(false);
    setError('');
  }, [prefill]);

  useEffect(() => {
    let cancelled = false;

    async function loadOptions() {
      setOptionsLoading(true);
      try {
        const location = user?.location || 'Six Flags Saint Louis';
        const [optionsData, supervisorsData] = await Promise.all([
          getCompensationOptions(location),
          getApprovingSupervisors(),
        ]);
        if (!cancelled) {
          const raw = optionsData.options || [];
          const labels = raw
            .map((entry) =>
              typeof entry === 'string' ? entry.trim() : String(entry?.option || '').trim()
            )
            .filter((label) => label && label !== '[object Object]');
          setResolutionOptions(labels);
          setApprovingSupervisors(supervisorsData.supervisors || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.error || 'Unable to load compensation options');
        }
      } finally {
        if (!cancelled) {
          setOptionsLoading(false);
        }
      }
    }

    loadOptions();
    return () => {
      cancelled = true;
    };
  }, [user?.location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'resolution') {
      setFormData((prev) => clearResolutionFields(prev, value));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const setTicketType = (ticketType) => {
    setFormData((prev) => ({ ...prev, ticketType }));
  };

  const validateForm = () => {
    if (!formData.guestName.trim()) return 'Guest name is required';
    if (!formData.guestEmail.trim()) return 'Guest email is required';
    if (!formData.ticketType) {
      return 'Please select Six Flags or Enchanted Parks tickets/passes';
    }
    if (!formData.ticketId.trim()) return 'Ticket ID is required';
    if (!formData.guestConcern.trim()) return 'Guest concern is required';
    if (!formData.resolution) return 'Please select a resolution';
    if (formData.resolution === 'Other' && !formData.resolutionOther.trim()) {
      return 'Please describe the compensation given';
    }
    if (formData.resolution === 'Refund Request') {
      if (!formData.purchaseDate.trim()) return 'Purchase date is required';
      if (!formData.purchaseTime.trim()) return 'Purchase time is required';
      if (!/^\d{4}$/.test(formData.ccLast4.trim())) {
        return 'Last 4 digits of the credit card are required (4 digits)';
      }
      if (!formData.nameOnOrder.trim()) return 'Name on the order is required';
      if (!formData.approvingSupervisorId) {
        return 'Approving supervisor is required';
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    try {
      await submitRecord({
        userId: user.id,
        guestName: formData.guestName.trim(),
        guestEmail: formData.guestEmail.trim(),
        ticketType: formData.ticketType,
        ticketId: formData.ticketId.trim(),
        guestConcern: formData.guestConcern.trim(),
        resolution: formData.resolution,
        resolutionOther:
          formData.resolution === 'Other' ? formData.resolutionOther.trim() : '',
        purchaseDate:
          formData.resolution === 'Refund Request' ? formData.purchaseDate.trim() : '',
        purchaseTime:
          formData.resolution === 'Refund Request' ? formData.purchaseTime.trim() : '',
        ccLast4: formData.resolution === 'Refund Request' ? formData.ccLast4.trim() : '',
        nameOnOrder:
          formData.resolution === 'Refund Request' ? formData.nameOnOrder.trim() : '',
        approvingSupervisorId:
          formData.resolution === 'Refund Request'
            ? formData.approvingSupervisorId
            : '',
      });

      setSuccess(true);
      setFormData(initialFormData);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.error || 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container wide-container">
      <AppHeader
        user={user}
        pageTitle="New Submission"
        onNavigate={onNavigate}
        onLogout={onLogout}
        active="submit"
      />

      {error && <div className="error">{error}</div>}
      {success && <div className="success">✓ Submission saved successfully!</div>}

      {prefill && !success && (
        <p className="subtitle" style={{ marginBottom: '16px' }}>
          Guest details were filled in from an existing record. Enter the new concern and
          resolution below.
        </p>
      )}

      {!success ? (
        <form onSubmit={handleSubmit} className="submit-form">
          <div className="form-group">
            <label htmlFor="guestName">Guest name *</label>
            <input
              type="text"
              id="guestName"
              name="guestName"
              value={formData.guestName}
              onChange={handleChange}
              placeholder="Enter guest full name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="guestEmail">Guest email address *</label>
            <input
              type="email"
              id="guestEmail"
              name="guestEmail"
              value={formData.guestEmail}
              onChange={handleChange}
              placeholder="guest@example.com"
              required
            />
          </div>

          <div className="form-group">
            <label>Guest ticket / pass type *</label>
            <div className="toggle-group">
              {TICKET_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`toggle-btn ${formData.ticketType === type ? 'active' : ''}`}
                  onClick={() => setTicketType(type)}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="ticketId">Ticket ID *</label>
            <input
              type="text"
              id="ticketId"
              name="ticketId"
              value={formData.ticketId}
              onChange={handleChange}
              placeholder="Enter ticket or pass ID"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="guestConcern">Guest concern *</label>
            <textarea
              id="guestConcern"
              name="guestConcern"
              value={formData.guestConcern}
              onChange={handleChange}
              placeholder="Describe the guest's concern..."
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="resolution">Resolution *</label>
            <select
              id="resolution"
              name="resolution"
              value={formData.resolution}
              onChange={handleChange}
              required
              disabled={optionsLoading}
            >
              <option value="">
                {optionsLoading ? 'Loading options...' : 'Select a resolution...'}
              </option>
              {resolutionOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {user?.location && (
              <p className="subtitle" style={{ marginTop: '6px' }}>
                Options for {user.location}
              </p>
            )}
          </div>

          {formData.resolution === 'Other' && (
            <div className="form-group">
              <label htmlFor="resolutionOther">Compensation given *</label>
              <input
                type="text"
                id="resolutionOther"
                name="resolutionOther"
                value={formData.resolutionOther}
                onChange={handleChange}
                placeholder="Describe what compensation was given"
                required
              />
            </div>
          )}

          {formData.resolution === 'Refund Request' && (
            <div className="refund-fields">
              <h3 style={{ marginBottom: '12px' }}>Refund request details</h3>
              <div className="form-group">
                <label htmlFor="purchaseDate">Purchase date *</label>
                <input
                  type="date"
                  id="purchaseDate"
                  name="purchaseDate"
                  value={formData.purchaseDate}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="purchaseTime">Purchase time *</label>
                <input
                  type="time"
                  id="purchaseTime"
                  name="purchaseTime"
                  value={formData.purchaseTime}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="ccLast4">Last 4 of credit card used *</label>
                <input
                  type="text"
                  id="ccLast4"
                  name="ccLast4"
                  value={formData.ccLast4}
                  onChange={handleChange}
                  placeholder="1234"
                  maxLength={4}
                  pattern="\d{4}"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="nameOnOrder">Name on the order *</label>
                <input
                  type="text"
                  id="nameOnOrder"
                  name="nameOnOrder"
                  value={formData.nameOnOrder}
                  onChange={handleChange}
                  placeholder="Name as it appears on the order"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="approvingSupervisorId">Supervisor that approved the refund *</label>
                <select
                  id="approvingSupervisorId"
                  name="approvingSupervisorId"
                  value={formData.approvingSupervisorId}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select approving supervisor...</option>
                  {approvingSupervisors.map((supervisor) => (
                    <option key={supervisor.id} value={supervisor.id}>
                      {supervisor.name} ({supervisor.id})
                    </option>
                  ))}
                </select>
                <p className="subtitle" style={{ marginTop: '6px' }}>
                  Active Admissions department supervisors only
                </p>
              </div>
            </div>
          )}

          <button type="submit" disabled={loading || optionsLoading}>
            {loading ? 'Submitting...' : 'Submit Record'}
          </button>
          <p className="subtitle" style={{ marginTop: '15px', textAlign: 'center' }}>
            * Required fields
          </p>
        </form>
      ) : (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <p style={{ marginBottom: '15px' }}>Your record has been saved successfully.</p>
          <button className="btn-primary" onClick={() => setSuccess(false)}>
            Add Another
          </button>
          <button
            className="btn-secondary"
            onClick={() => onNavigate('dashboard')}
            style={{ marginLeft: '10px' }}
          >
            Back to Dashboard
          </button>
        </div>
      )}
    </div>
  );
}

export default Submit;
